import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const WishlistContext = createContext();

// Wishlist reducer
const wishlistReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_WISHLIST':
      return {
        ...state,
        items: action.payload.items || []
      };
    
    case 'ADD_TO_WISHLIST':
      const existingItem = state.items.find(item => 
        item.id === action.payload.id && item.type === action.payload.type
      );
      if (existingItem) {
        return state; // Item already in wishlist
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        items: state.items.filter(item => 
          !(item.id === action.payload.id && item.type === action.payload.type)
        )
      };

    case 'CLEAR_WISHLIST':
      return {
        ...state,
        items: []
      };

    default:
      return state;
  }
};

// Wishlist provider component
export const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, {
    items: []
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const savedWishlist = localStorage.getItem('wishlist');
    if (savedWishlist) {
      try {
        const parsedWishlist = JSON.parse(savedWishlist);
        dispatch({ type: 'LOAD_WISHLIST', payload: parsedWishlist });
      } catch (error) {
        console.error('Error parsing saved wishlist:', error);
        localStorage.removeItem('wishlist');
      }
    }
    setIsInitialized(true);
  }, []);

  // Save wishlist to localStorage whenever it changes (but not during initial load)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('wishlist', JSON.stringify(state));
    }
  }, [state, isInitialized]);

  // Wishlist actions
  const addToWishlist = (item) => {
    dispatch({ type: 'ADD_TO_WISHLIST', payload: item });
  };

  const removeFromWishlist = (item) => {
    dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: item });
  };

  const clearWishlist = () => {
    dispatch({ type: 'CLEAR_WISHLIST' });
  };

  const isInWishlist = (id, type) => {
    return state.items.some(item => item.id === id && item.type === type);
  };

  const getWishlistCount = () => {
    return state.items.length;
  };

  const getWishlistByType = (type) => {
    return state.items.filter(item => item.type === type);
  };

  const value = {
    ...state,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    isInWishlist,
    getWishlistCount,
    getWishlistByType
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

// Custom hook to use wishlist context
export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}; 